/**
 * @fileoverview Parser for human-readable move notation
 *
 * Parses move notation like "Light Bear A1->B2:capture" into structured format.
 *
 * Notation Format:
 *   {Color} {PieceType} {From}->{To}[:{MoveType}]
 *
 * Examples:
 *   "Light Bear A1->B2"           - Regular move
 *   "Dark Frog C5->C7:lilypad"    - Special move type
 *   "Light Monkey D3->D5:swing"   - Swing (triggers bonus turn)
 *   "Dark Penguin E4->E4:rotate"  - Rotate in place
 *
 * Coordinate System:
 *   Columns: A-E = 0-4
 *   Rows: 1-8 = 0-7 (A1 = bottom-left = row 0, col 0)
 */

import { Position, MoveType, PieceType } from "../zodiacEngine/types";
import { ParsedMove, MOVE_TYPE_SPECIFIERS, PIECE_TYPE_NAMES } from "./types";

/**
 * Parse a human-readable move notation string
 *
 * @param notation - Move string like "Light Bear A1->B2:capture" or special commands like "timeout:light"
 * @returns ParsedMove object with all move details
 * @throws Error if notation is invalid
 */
export function parseMove(notation: string): ParsedMove {
  const trimmed = notation.trim();

  // Check for special commands first (e.g., "timeout:light", "timeout:dark")
  const timeoutPattern = /^timeout:(light|dark)$/i;
  const timeoutMatch = trimmed.match(timeoutPattern);
  if (timeoutMatch) {
    const color = timeoutMatch[1].toLowerCase() as "light" | "dark";
    // Return a dummy ParsedMove with special command flag
    return {
      color,
      pieceType: PieceType.Bear, // Dummy value (unused for special commands)
      fromPosition: { row: 0, col: 0 }, // Dummy value
      toPosition: { row: 0, col: 0 }, // Dummy value
      moveType: MoveType.regular, // Dummy value
      rawNotation: notation,
      isSpecialCommand: true,
      specialCommand: "timeout",
    };
  }

  // Check for movezombieshadow command (player picks wrong shadow, clears shadows, ends turn)
  if (trimmed.toLowerCase() === "movezombieshadow") {
    return {
      color: "light", // Dummy - the current player is whoever's turn it is
      pieceType: PieceType.Bear, // Dummy value (unused for special commands)
      fromPosition: { row: 0, col: 0 }, // Dummy value
      toPosition: { row: 0, col: 0 }, // Dummy value
      moveType: MoveType.regular, // Dummy value
      rawNotation: notation,
      isSpecialCommand: true,
      specialCommand: "movezombieshadow",
    };
  }

  // Regex pattern for move notation
  // Groups: (1)Color (2)PieceType (3)FromCoord (4)ToCoord (5)?MoveType
  // Note: MoveType uses [\w-]+ to support hyphenated specifiers like "entangle-up"
  const pattern = /^(Light|Dark)\s+(\w+)\s+([A-Ea-e][1-8])\s*->\s*([A-Ea-e][1-8])(?::([\w-]+))?$/i;

  const match = trimmed.match(pattern);
  if (!match) {
    throw new Error(
      `Invalid move notation: "${notation}". Expected format: "Light Bear A1->B2" or "Dark Frog C5->C7:lilypad" or "timeout:light"`
    );
  }

  const [, colorStr, pieceTypeStr, fromCoord, toCoord, moveTypeStr] = match;

  // Parse color
  const color = colorStr.toLowerCase() as "light" | "dark";

  // Parse piece type
  const pieceType = parsePieceType(pieceTypeStr);

  // Parse positions
  const fromPosition = parseCoordinate(fromCoord);
  const toPosition = parseCoordinate(toCoord);

  // Parse move type (default to regular)
  const moveType = parseMoveType(moveTypeStr || "");

  // Parse entangle direction if present (e.g., "entangle-up" -> "up")
  const entangleDirection = parseEntangleDirection(moveTypeStr || "");

  return {
    color,
    pieceType,
    fromPosition,
    toPosition,
    moveType,
    rawNotation: notation,
    entangleDirection,
  };
}

/**
 * Parse algebraic coordinate to Position
 *
 * @param coord - Coordinate like "A1", "E8", "c3"
 * @returns Position object { row, col }
 */
export function parseCoordinate(coord: string): Position {
  if (coord.length !== 2) {
    throw new Error(`Invalid coordinate: "${coord}". Expected format: "A1" to "E8"`);
  }

  const colChar = coord[0].toUpperCase();
  const rowChar = coord[1];

  // Column: A=0, B=1, C=2, D=3, E=4
  const col = colChar.charCodeAt(0) - "A".charCodeAt(0);
  if (col < 0 || col > 4) {
    throw new Error(`Invalid column "${colChar}". Must be A-E`);
  }

  // Row: 1=0, 2=1, ... 8=7 (1-indexed to 0-indexed)
  const row = parseInt(rowChar, 10) - 1;
  if (isNaN(row) || row < 0 || row > 7) {
    throw new Error(`Invalid row "${rowChar}". Must be 1-8`);
  }

  return { row, col };
}

/**
 * Convert Position to algebraic notation
 *
 * @param position - Position object { row, col }
 * @returns Algebraic coordinate like "A1", "E8"
 */
export function positionToCoordinate(position: Position): string {
  const colChar = String.fromCharCode("A".charCodeAt(0) + position.col);
  const rowNum = position.row + 1;
  return `${colChar}${rowNum}`;
}

/**
 * Parse piece type string to PieceType enum
 *
 * @param typeStr - Piece type string like "Bear", "Frog"
 * @returns PieceType enum value
 */
export function parsePieceType(typeStr: string): PieceType {
  const normalized = typeStr.toLowerCase();
  const pieceType = PIECE_TYPE_NAMES[normalized];

  if (!pieceType) {
    const validTypes = Object.keys(PIECE_TYPE_NAMES).join(", ");
    throw new Error(`Invalid piece type: "${typeStr}". Valid types: ${validTypes}`);
  }

  return pieceType;
}

/**
 * Parse move type specifier to MoveType enum
 *
 * @param specifier - Move type specifier like "capture", "lilypad", ""
 * @returns MoveType enum value
 */
export function parseMoveType(specifier: string): MoveType {
  const normalized = specifier.toLowerCase();
  const moveType = MOVE_TYPE_SPECIFIERS[normalized];

  if (moveType === undefined) {
    const validTypes = Object.keys(MOVE_TYPE_SPECIFIERS)
      .filter((k) => k !== "")
      .join(", ");
    throw new Error(`Invalid move type: "${specifier}". Valid types: ${validTypes}`);
  }

  return moveType;
}

/**
 * Extract entangle direction from specifier if present
 *
 * @param specifier - Move type specifier like "entangle-up", "entangle-down"
 * @returns Direction string or undefined
 */
export function parseEntangleDirection(specifier: string): "up" | "down" | "left" | "right" | undefined {
  const normalized = specifier.toLowerCase();

  if (normalized.startsWith("entangle-")) {
    const direction = normalized.substring("entangle-".length);
    if (direction === "up" || direction === "down" || direction === "left" || direction === "right") {
      return direction;
    }
  }

  return undefined;
}

/**
 * Format a parsed move back to notation string
 *
 * @param move - ParsedMove object
 * @returns Human-readable notation string
 */
export function formatMove(move: ParsedMove): string {
  const color = move.color === "light" ? "Light" : "Dark";
  const from = positionToCoordinate(move.fromPosition);
  const to = positionToCoordinate(move.toPosition);

  let notation = `${color} ${move.pieceType} ${from}->${to}`;

  // Add move type specifier if not regular
  if (move.moveType !== MoveType.regular) {
    const specifier = Object.entries(MOVE_TYPE_SPECIFIERS).find(
      ([, type]) => type === move.moveType
    )?.[0];
    if (specifier) {
      notation += `:${specifier}`;
    }
  }

  return notation;
}

/**
 * Validate that a move notation is syntactically correct
 *
 * @param notation - Move notation string
 * @returns true if valid, false otherwise
 */
export function isValidNotation(notation: string): boolean {
  try {
    parseMove(notation);
    return true;
  } catch {
    return false;
  }
}

/**
 * Generate a move notation from component parts
 *
 * @param color - "light" or "dark"
 * @param pieceType - PieceType enum
 * @param from - Starting position
 * @param to - Ending position
 * @param moveType - Optional MoveType (defaults to regular)
 * @returns Formatted notation string
 */
export function createMoveNotation(
  color: "light" | "dark",
  pieceType: PieceType,
  from: Position,
  to: Position,
  moveType: MoveType = MoveType.regular
): string {
  return formatMove({
    color,
    pieceType,
    fromPosition: from,
    toPosition: to,
    moveType,
    rawNotation: "",
  });
}

/**
 * Parse multiple moves from a semicolon-separated string
 *
 * @param movesString - Semicolon-separated moves like "Light Bear A1->B2; Dark Frog C3->C5"
 * @returns Array of ParsedMove objects
 */
export function parseMoves(movesString: string): ParsedMove[] {
  return movesString
    .split(";")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(parseMove);
}
